import { CompanionResumeCard } from '@/components/companion/CompanionResumeCard';
import { useAuth } from '@/contexts/auth-context';
import { useAffiliateInvitationStatsQuery } from '@/hooks/queries/use-affiliate-invitation-stats-query';
import { useAffiliatesCountQuery } from '@/hooks/queries/use-affiliates-count-query';
import { useApplicationsPendingCountQuery } from '@/hooks/queries/use-applications-query';
import { useMessagingSectionCountersQuery } from '@/hooks/queries/use-messaging-section-counters-query';
import { useUnseenMentionsCountQuery } from '@/hooks/queries/use-unseen-mentions-count-query';
import { useUgcAttentionQuery } from '@/hooks/queries/use-ugc-attention-query';
import { useUgcAvailability } from '@/hooks/use-ugc-availability';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useIsBrandManager } from '@/hooks/use-is-brand-manager';
import { usePlanGates } from '@/hooks/use-plan-gates';
import { isBoostDevPreviewActive } from '@/libs/boostDevPreview';
import {
  AffiliationLinkIcon,
  ApplicationsIcon,
  BrandSpaceIcon,
  CollabsIcon,
  CreatorsIcon,
  DashboardIcon,
  FlowsIcon,
  GiftIcon,
  InboxIcon,
  MentionsIcon,
  navIconNode,
  SettingsIcon,
} from '@/components/icons';
import { useGiftingAccess } from '@/hooks/use-gifting-access';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { FeatureFlag, PlanLimit } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AssistantsConnectCard } from './AssistantsConnectCard';
import BrandSpaceLiveBadge from './BrandSpaceLiveBadge';
import { FreePlanCard } from './FreePlanCard';
import BrandSelector from '../nav-bar/components/BrandSelector';
import NavGroup from '../nav-bar/components/NavGroup';
import type { SubNavItem } from '../nav-bar/components/NavSubLink';
import SidebarSectionLabel from '../nav-bar/components/SidebarSectionLabel';
import NavLink from '../nav-bar/nav-link';
import { buildAffiliationNavItems } from './affiliation-nav-items';

/**
 * The default sidebar surface: brand switcher, MAIN + SOCIAL sections (with the
 * inline Collabs / Affiliation groups) and the footer. Always mounted so the
 * slide-back from an internal panel reads as a single motion.
 */
const MainNav: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { flowsLocked, openUpsell } = usePlanGates();

  // Collabs always shows its destinations; the COLLABS flag decides which are
  // reachable, and the UGC offer decides the UGC page. A brand without it sees tracking and brief templates greyed
  // and marked as coming, which is true for it — while the two organisations
  // that already have the flag keep using those pages, so nothing is announced
  // as upcoming to somebody who uses it today.
  const coPublicitesEnabled =
    useFeatureFlag(FeatureFlag.PARTNERSHIP_ADS_BOOST) || isBoostDevPreviewActive();
  const briefsApplicationsEnabled = useFeatureFlag(FeatureFlag.BRIEF_APPLICATIONS);
  const collabsFlagEnabled = useFeatureFlag(FeatureFlag.COLLABS);
  const collabsEnabled = coPublicitesEnabled || collabsFlagEnabled;

  const { data: counters } = useMessagingSectionCountersQuery(user?.currentBrandId);
  const inboxUnread = counters?.inboxUnread ?? 0;

  const { data: affiliatesCountData } = useAffiliatesCountQuery({ status: 'active' });
  const affiliatesCount = affiliatesCountData?.count ?? 0;

  const { canManage: canManageBrand } = useIsBrandManager();
  const { data: invitationStats } = useAffiliateInvitationStatsQuery(user?.currentBrandId);
  const pendingInvitations = invitationStats?.pending ?? 0;
  // Briefs and programs together; only a brand manager reviews applications.
  const { data: pendingApplications = 0 } = useApplicationsPendingCountQuery(
    user?.currentBrandId,
    canManageBrand,
    { briefs: briefsApplicationsEnabled }
  );

  const { data: unseenMentionsCount } = useUnseenMentionsCountQuery();

  // Gifting needs a connected Shopify store plus a producer of gifts; anything
  // but a settled yes hides the entry, so it never flashes for a brand that
  // cannot reach the page.
  const giftingAccess = useGiftingAccess();

  // A brand that lost the UGC offer (flag closed, Shopify disconnected) keeps
  // following the UGC collabs it has: the page stays reachable, read only.
  const ugcAvailable = useUgcAvailability();
  const { data: ugcAttention } = useUgcAttentionQuery({ enabled: collabsFlagEnabled });
  const ugcAttentionCount = ugcAttention?.count ?? 0;
  const ugcReachable = ugcAvailable || (ugcAttention?.ugcCollabCount ?? 0) > 0;

  const comingSoon = { disabled: true, tag: t('navigation.comingSoonBadge') } as const;
  const collabsItems: SubNavItem[] = [
    {
      label: t('collabs.tracking.navLabel'),
      to: '/collabs/suivi',
      ...(collabsFlagEnabled ? {} : comingSoon),
    },
    // The UGC page, badged with the decisions due today and the choices only the
    // brand can make. A brand never offered UGC sees it announced as coming.
    ugcReachable
      ? { label: t('navigation.ugc'), to: '/collabs/ugc', badge: ugcAttentionCount || undefined }
      : { label: t('navigation.ugc'), to: '/collabs/ugc', ...comingSoon },
    ...(coPublicitesEnabled
      ? [{ label: t('collabs.coPublicites'), to: '/collabs', exact: true }]
      : []),
    {
      label: t('collabs.briefTemplates.navLabel'),
      to: '/collabs/brief-templates',
      ...(collabsFlagEnabled ? {} : comingSoon),
    },
  ];

  const affiliationItems = buildAffiliationNavItems({
    labels: {
      programs: t('navigation.programs'),
      affiliates: t('navigation.affiliates'),
      invitations: t('navigation.invitations'),
      commissions: t('navigation.commissions'),
    },
    affiliatesCount,
    pendingInvitations,
  });

  return (
    <Flex direction="column" h="full" px={3} pt={4} pb="14px">
      <BrandSelector />

      <Box flex={1} overflowY="auto" pt="18px" pb={2}>
        <VStack align="stretch" gap={4}>
          <Box>
            <SidebarSectionLabel>{t('navigation.sections.main')}</SidebarSectionLabel>
            <VStack align="stretch" gap="1px">
              <NavLink
                to="/dashboard"
                label={t('navigation.dashboard')}
                icon={navIconNode(DashboardIcon)}
              />
              <NavLink
                to="/messages"
                label={t('navigation.inbox')}
                icon={navIconNode(InboxIcon)}
                badge={inboxUnread || undefined}
              />
              {canManageBrand && (
                <NavLink
                  to="/applications"
                  label={t('navigation.applications')}
                  icon={navIconNode(ApplicationsIcon)}
                  badge={pendingApplications || undefined}
                />
              )}
              {collabsEnabled && (
                <NavGroup
                  label={t('navigation.collabs')}
                  icon={navIconNode(CollabsIcon)}
                  items={collabsItems}
                  tag={t('navigation.newBadge')}
                />
              )}
              <NavGroup
                label={t('navigation.affiliation')}
                icon={navIconNode(AffiliationLinkIcon)}
                items={affiliationItems}
              />
              {giftingAccess === 'granted' && (
                <NavLink
                  to="/gifting"
                  label={t('gifting.nav')}
                  icon={navIconNode(GiftIcon)}
                  tag={t('navigation.newBadge')}
                />
              )}
              <NavLink
                to="/flows"
                label={t('navigation.flows')}
                icon={navIconNode(FlowsIcon)}
                // Without Flows in its plan, the row presents them rather than
                // opening a page whose every action the API refuses.
                onClick={flowsLocked ? () => openUpsell(PlanLimit.FLOWS) : undefined}
              />
            </VStack>
          </Box>

          <Box>
            <SidebarSectionLabel>{t('navigation.sections.social')}</SidebarSectionLabel>
            <VStack align="stretch" gap="1px">
              <NavLink
                to="/creators/discovery"
                label={t('navigation.creatorDiscovery')}
                icon={navIconNode(CreatorsIcon)}
              />
              <NavLink
                to="/mentions"
                label={t('navigation.mentions')}
                icon={navIconNode(MentionsIcon)}
                badge={unseenMentionsCount || undefined}
              />
            </VStack>
          </Box>
        </VStack>
      </Box>

      <VStack align="stretch" gap={2} mb={3}>
        <CompanionResumeCard />
        <FreePlanCard />
        <AssistantsConnectCard />
      </VStack>

      <VStack
        align="stretch"
        gap="1px"
        pt="10px"
        borderTop="1px solid"
        borderColor="navDark.footerBorder"
      >
        {/*
          Brand Space sits in the footer block, not in MAIN. Its access rules are
          unchanged by the move: an active brand plus brand-manager rights.
        */}
        {user?.currentBrandId && canManageBrand ? (
          <NavLink
            to="/brand-space"
            label={t('navigation.brandSpace')}
            icon={navIconNode(BrandSpaceIcon)}
            rightChildren={<BrandSpaceLiveBadge />}
            mb="8px"
          />
        ) : null}
        <NavLink to="/settings" label={t('navigation.settings')} icon={navIconNode(SettingsIcon)} />
      </VStack>
    </Flex>
  );
};

export default MainNav;
